/**
 * Provider Migration Helper
 * This file provides compatibility shims to help migrate from React Context to Zustand stores
 */

import React from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useAuthStore, useShopStore, useBookingStore } from 'stores';
import routeStore from './routeStore';

// Auth Context Migration
export const useAuthContext = () => {
  const authStore = useAuthStore();

  // Map old AuthContext API to new store
  return {
    authService: {
      loginWithEmailPassword: authStore.loginWithEmailPassword,
      logout: authStore.logout,
      isAuthenticated: () => authStore.isAuthenticated,
      generateRedirectUrl: authStore.generateRedirectUrl,
      getRedirectUrl: authStore.getRedirectUrl,
      resetRedirect: authStore.resetRedirect,
      register: authStore.register,
      verifyRegistration: authStore.verifyRegistration,
      resendOTP: authStore.resendOTP,
      forgotPassword: authStore.forgotPassword,
      resetPassword: authStore.resetPassword,
      userLoginWithGoogle: authStore.userLoginWithGoogle,
      removeAccount: authStore.removeAccount,
      authToken: authStore.authToken,
    },
    currentUser: authStore.currentUser,
    $currentUser: authStore.setCurrentUser,
    user: authStore.user,
    isAuthenticated: () => authStore.isAuthenticated,
    authToken: authStore.authToken,
    loadingToken: authStore.loadingToken,
    isAuthLoading: authStore.isAuthLoading,
    authError: authStore.authError,
  };
};

// Page Context Migration
export const usePageContext = () => {
  const shopStore = useShopStore();
  const authStore = useAuthStore();

  return {
    isSSR: shopStore.isSSR,
    shopStaff: shopStore.shopStaff,
    shop: shopStore.shop,
    $shop: shopStore.setShop,
    pageServices: {
      loadShopStaff: () => shopStore.loadShopStaff(authStore.user?.id),
      loadMonthlyRevenue: () => shopStore.loadMonthlyRevenue(
        new Date().getMonth() + 1,
        new Date().getFullYear()
      ),
      autoSelectShop: shopStore.autoSelectShop,
      onChangeShop: shopStore.onChangeShop,
    },
  };
};

// Route Context Migration
export const useRouteContext = () => {
  const router = useRouter();

  return {
    router,
    routeStore,
  };
};

// Booking Context Migration
export const useBookingContext = () => {
  const bookingStore = useBookingStore();
  const shopStore = useShopStore();

  return {
    bookingService: {
      loadBookingSlots: (day?: any) => bookingStore.loadBookingSlots(day, shopStore.shop?.id),
      loadShopDayOffs: (day?: any) => bookingStore.loadShopDayOffs(day, shopStore.shop?.id),
      onChangeDay: (day: any) => bookingStore.onChangeDay(day, shopStore.shop?.id),
      values: bookingStore.formValues,
      setSlotUpdateLoading: bookingStore.setSlotUpdateLoading,
    },
    bookingSlots: bookingStore.bookingSlots,
    shopDayOffs: bookingStore.shopDayOffs,
    isLoadingSlotsData: bookingStore.isLoadingSlotsData,
    isLoadingDayOffs: bookingStore.isLoadingDayOffs,
    slotUpdateLoading: bookingStore.slotUpdateLoading,
  };
};

// Modal Context Migration - Temporary stub for components that haven't been migrated yet
export const useModalContext = () => {
  return {
    modalService: {
      visible: false,
      addDOM: (element: any) => {},
      removeDOM: (delay?: number) => {},
      hide: () => {},
      setVisible: (visible: boolean) => {},
      onMoreCard: (booking: any) => {},
    },
    domModals: [],
    visible: false,
  };
};

// Form Context Migration
// For Formik, we'll use it directly without a provider wrapper
export { useFormik, useField, useFormikContext as useFormContext } from 'formik';

// Re-export Next.js navigation hooks for convenience
export { useRouter, useSearchParams, usePathname };

// Compatibility wrappers for gradual migration
export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  // Modal state is managed by Zustand store, no need for provider
  return <>{children}</>;
};

// ViewModal as a higher-order component that doesn't use hooks
export const ViewModal = ({ modalId, children }: { modalId: string; children: React.ReactNode }) => {
  // This will be handled by parent components that use the hook
  // For now, just render children - parent should control visibility
  return <>{children}</>;
};